import { useState, useEffect } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeasonalDecorations() {
  const [decorations, setDecorations] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    const fetchActive = async () => {
      try {
        const res = await axios.get(`${API}/decorations/active`);
        setDecorations(res.data);
      } catch (err) {
        // Silent fail — decorations are non-critical
      }
    };
    fetchActive();
    // Re-check every 5 minutes for schedule changes
    const interval = setInterval(fetchActive, 300000);
    return () => clearInterval(interval);
  }, []);

  const dismiss = (id) => {
    setDismissed(prev => [...prev, id]);
  };

  const visible = decorations.filter(d => !dismissed.includes(d.id));
  if (visible.length === 0) return null;

  // Separate by position
  const banners = visible.filter(d => d.position === "top-banner");
  const corners = visible.filter(d => d.position?.startsWith("corner-"));
  const floating = visible.filter(d => d.position?.startsWith("floating-"));

  return (
    <>
      {/* Top Banners */}
      {banners.map(dec => (
        <div
          key={dec.id}
          className="relative w-full flex items-center justify-center text-sm font-medium animate-in slide-in-from-top duration-500"
          style={{
            backgroundColor: dec.styles?.background_color || "#1a1a2e",
            color: dec.styles?.text_color || "#ffffff",
            height: dec.styles?.height || "50px",
            opacity: dec.styles?.opacity ?? 1,
            backgroundImage: dec.image_url ? `url(${resolveImageUrl(dec.image_url)})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid={`seasonal-banner-${dec.id}`}
        >
          <span
            className="px-4 py-1 rounded-md"
            style={{
              backgroundColor: dec.image_url ? `${dec.styles?.background_color || "#1a1a2e"}bb` : "transparent"
            }}
          >
            {dec.styles?.text || dec.name}
          </span>
          <button
            onClick={() => dismiss(dec.id)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            data-testid={`dismiss-banner-${dec.id}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}

      {/* Corner Decorations */}
      {corners.map(dec => {
        const posMap = {
          "corner-top-left": "top-24 left-4",
          "corner-top-right": "top-24 right-4",
          "corner-bottom-left": "bottom-4 left-4",
          "corner-bottom-right": "bottom-4 right-4",
        };
        return (
          <div
            key={dec.id}
            className={`fixed ${posMap[dec.position] || "top-24 right-4"} z-40 pointer-events-auto`}
            style={{ opacity: dec.styles?.opacity ?? 1 }}
            data-testid={`seasonal-corner-${dec.id}`}
          >
            {dec.image_url ? (
              <img src={resolveImageUrl(dec.image_url)} alt={dec.name} className="w-20 h-20 object-contain drop-shadow-lg" />
            ) : (
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-xs font-bold shadow-lg"
                style={{ backgroundColor: dec.styles?.background_color || "#c41e3a" }}
              >
                {dec.styles?.text?.slice(0, 8) || dec.name?.slice(0, 8)}
              </div>
            )}
            <button
              onClick={() => dismiss(dec.id)}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}

      {/* Floating Decorations */}
      {floating.map(dec => {
        const isLeft = dec.position === "floating-left";
        return (
          <div
            key={dec.id}
            className={`fixed ${isLeft ? "left-4" : "right-4"} top-1/2 -translate-y-1/2 z-40`}
            style={{ opacity: dec.styles?.opacity ?? 0.85 }}
            data-testid={`seasonal-floating-${dec.id}`}
          >
            {dec.image_url ? (
              <img src={resolveImageUrl(dec.image_url)} alt={dec.name} className="w-16 h-auto object-contain drop-shadow-lg" />
            ) : (
              <div
                className="w-12 h-24 rounded-xl flex items-center justify-center text-white text-xs font-bold shadow-lg"
                style={{
                  backgroundColor: dec.styles?.background_color || "#c41e3a",
                  writingMode: "vertical-rl",
                }}
              >
                {dec.styles?.text || dec.name}
              </div>
            )}
            <button
              onClick={() => dismiss(dec.id)}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}
    </>
  );
}
